import { useState, useEffect, useCallback, useRef } from "react";

export function useDragRowSelection<T extends { id: string }>(items: T[]) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const isDraggingRef = useRef(false);
  const dragTargetStateRef = useRef<boolean>(true);
  const lastSelectedIndexRef = useRef<number | null>(null);

  // Global mouseup listener to stop dragging
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      isDraggingRef.current = false;
    };

    window.addEventListener("mouseup", handleGlobalMouseUp);
    return () => {
      window.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, []);

  const isAllSelected =
    items.length > 0 && items.every(item => selectedIds.includes(item.id));
  const isIndeterminate =
    items.some(item => selectedIds.includes(item.id)) && !isAllSelected;

  const handleSelectAll = useCallback(
    (e: { target: { checked: boolean } }) => {
      if (e.target.checked) {
        const pageIds = items.map(item => item.id);
        setSelectedIds(prev => Array.from(new Set([...prev, ...pageIds])));
      } else {
        const pageIds = new Set(items.map(item => item.id));
        setSelectedIds(prev => prev.filter(id => !pageIds.has(id)));
      }
    },
    [items]
  );

  const handleMouseDown = useCallback(
    (id: string, index: number, event: React.MouseEvent) => {
      // Only handle left mouse click
      if (event.button !== 0) {
        return;
      }

      // Prevent native text selection while dragging
      event.preventDefault();

      const isCurrentlySelected = selectedIds.includes(id);

      // Handle Shift + Click range selection
      if (event.shiftKey && lastSelectedIndexRef.current !== null) {
        const start = Math.min(lastSelectedIndexRef.current, index);
        const end = Math.max(lastSelectedIndexRef.current, index);
        const rangeIds = items.slice(start, end + 1).map(item => item.id);

        setSelectedIds(prev => {
          const next = new Set(prev);
          rangeIds.forEach(rangeId => next.add(rangeId));
          return Array.from(next);
        });
        lastSelectedIndexRef.current = index;
        return;
      }

      // Start drag-to-select
      isDraggingRef.current = true;
      const targetState = !isCurrentlySelected;
      dragTargetStateRef.current = targetState;
      lastSelectedIndexRef.current = index;

      setSelectedIds(prev => {
        if (targetState) {
          return prev.includes(id) ? prev : [...prev, id];
        } else {
          return prev.filter(item => item !== id);
        }
      });
    },
    [items, selectedIds]
  );

  const handleMouseEnter = useCallback((id: string, index: number) => {
    if (!isDraggingRef.current) {
      return;
    }

    const targetState = dragTargetStateRef.current;
    lastSelectedIndexRef.current = index;

    setSelectedIds(prev => {
      if (targetState) {
        return prev.includes(id) ? prev : [...prev, id];
      } else {
        return prev.filter(item => item !== id);
      }
    });
  }, []);

  return {
    selectedIds,
    setSelectedIds,
    isAllSelected,
    isIndeterminate,
    handleSelectAll,
    handleMouseDown,
    handleMouseEnter,
  };
}
